import { Stack, TagGroup, Truncate, Text } from '@cad-arc';
import { CautionsTabAssociatedPersonsView } from '@mark43/cad-api';
import { LinkText } from '../../../../../common/components/link-text';
import { createCell, SimpleTable } from '../../../../../common/components/simple-table';
import { Tooltip } from '../../../../../common/components/tooltip';
import { useOpenDepartmentLink } from '../../../../../layout/use-open-department-link';
import { getMatchingCriteriaPersonNames } from './helpers';
import { MatchingCriteriaPersonPopover } from './matching-criteria-person-popover';
import { useApplicationType } from '../../../../../common/hooks/use-application-type';
import { EnhancedCaution, DEFAULT_CAUTION_PRIORITY } from '../enhanced-caution';

export function AssociatedPersonsCommonContent({
    data,
}: {
    data: CautionsTabAssociatedPersonsView | undefined;
}) {
    const openDepartmentLink = useOpenDepartmentLink();
    const { isFirstResponder } = useApplicationType();
    const firstColumnWidth = isFirstResponder ? '230px' : '250px';

    return (
        <SimpleTable
            hasBorders={false}
            columnStyles={[
                { width: firstColumnWidth },
                { width: `calc(100% - ${firstColumnWidth})` },
            ]}
            rows={[
                [createCell({ content: 'Name' }), createCell({ content: 'Match' })],
                ...(data?.cautionsTabAssociatedPeople || []).map((associatedPersonData) => {
                    const matchingCriteriaPersonNames = getMatchingCriteriaPersonNames(
                        associatedPersonData.matchingCriteriaPerson
                    );

                    return [
                        createCell({
                            content: (
                                <Stack key="name" spacing={1}>
                                    <LinkText
                                        data-testid="ASSOCIATED_PERSON_LINK"
                                        onClick={() => {
                                            openDepartmentLink({
                                                path: `profiles/persons/${associatedPersonData.id}/details`,
                                                app: 'RMS',
                                                tenantStrategy: 'PREFER_RMS_TENANT',
                                            });
                                        }}
                                    >
                                        {[
                                            associatedPersonData.lastName,
                                            associatedPersonData.firstName,
                                        ].join(', ')}
                                    </LinkText>
                                    <Truncate
                                        renderCounter={(truncatedCount) => {
                                            return (
                                                <Tooltip
                                                    mouseLeaveDelay={0.15}
                                                    overlay={
                                                        <TagGroup>
                                                            {associatedPersonData.cautions
                                                                .slice(-1 * truncatedCount)
                                                                .map((caution) => (
                                                                    <EnhancedCaution
                                                                        key={caution.id}
                                                                        label={caution.caution}
                                                                        priority={
                                                                            caution.priorityLevel ??
                                                                            DEFAULT_CAUTION_PRIORITY
                                                                        }
                                                                        dateEffectiveFrom={
                                                                            caution.dateEffectiveFrom
                                                                        }
                                                                        dateEffectiveTo={
                                                                            caution.dateEffectiveTo
                                                                        }
                                                                        description={
                                                                            caution.description
                                                                        }
                                                                        provenance={
                                                                            caution.provenance
                                                                        }
                                                                    />
                                                                ))}
                                                        </TagGroup>
                                                    }
                                                >
                                                    <Text
                                                        style={{
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                        fontSize="sm"
                                                        color="brand"
                                                    >
                                                        + {truncatedCount}
                                                    </Text>
                                                </Tooltip>
                                            );
                                        }}
                                    >
                                        {associatedPersonData.cautions.map((caution) => (
                                            <EnhancedCaution
                                                key={caution.id}
                                                label={caution.caution}
                                                priority={
                                                    caution.priorityLevel ??
                                                    DEFAULT_CAUTION_PRIORITY
                                                }
                                                dateEffectiveFrom={caution.dateEffectiveFrom}
                                                dateEffectiveTo={caution.dateEffectiveTo}
                                                description={caution.description}
                                                provenance={caution.provenance}
                                            />
                                        ))}
                                    </Truncate>
                                </Stack>
                            ),
                        }),
                        createCell({
                            content: (
                                <Text>
                                    {matchingCriteriaPersonNames}{' '}
                                    <MatchingCriteriaPersonPopover
                                        criteria={associatedPersonData.matchingCriteriaPerson}
                                    />
                                </Text>
                            ),
                        }),
                    ];
                }),
            ]}
        />
    );
}
